/**
 * The WhatsApp self-serve wizard's step vocabulary — ONE owner.
 *
 * Everything that needs to name a wizard screen reads it from here: the shell (which component to
 * render), `wizardCopy.js` (which keys need help text), and the node-tests (which iterate this
 * ACTUAL array rather than a hand-copied list). Same shape as the A2P wizard's `STEPS`
 * (src/pages/MyAccount/A2P/A2PRegistrationWizard.vue) — `{key, label, component}` — with one
 * addition: `terminal`, for the screens the practice cannot continue past.
 *
 * ── WHY IT IS A SEPARATE MODULE AND NOT AN ARRAY INSIDE THE SHELL ────────────────────────────────
 * The A2P wizard keeps its STEPS inside the .vue, which forces the node-test to MIRROR it and keeps
 * the mirror honest with a grep. Copy-completeness (decision CQ3) needs more than a grep: the test
 * has to walk the real key set and prove every key has all three copy fields and that no copy key
 * is an orphan. A plain .js module can be loaded and executed by the node-test (the phiMask.js
 * precedent), so there is no mirror to drift.
 *
 * ── HOW THESE KEYS RELATE TO THE BACKEND'S STEPS ─────────────────────────────────────────────────
 * They are NOT the same vocabulary and must not be conflated. The backend's `state.js` STEPS are
 * onboarding PROGRESS (`not_started → triage → number_pending → …`); these are SCREENS. One backend
 * step can map to several screens (`not_started` covers intro/triage/confirm — all three are before
 * the single my-triage POST), and one screen can serve several backend steps (`progress` covers the
 * whole `waba_connected…template_submitted` band). The mapping itself lives in ONE pure function,
 * `deriveWizardStep` in src/store/whatsappSelfStart.js, and a node-test pins that every key it can
 * return exists in this array.
 */

/**
 * Order is the flow. Keys are how the rest of the wizard refers to screens — never an index.
 *
 * `terminal: true` means there is nowhere to continue TO: the wizard is finished (live), it has
 * been handed to a person (handoff_pending), or it can never run for this practice (blocked).
 * Terminal screens still carry the full copy trio — a practice who lands on one deserves to know
 * what happened and what, if anything, is expected of them.
 */
export const WIZARD_STEPS = [
  { key: 'intro',           label: 'What you get',            component: 'WizardIntroStep',          terminal: false },
  { key: 'triage',          label: 'Your phone number',       component: 'WizardTriageStep',         terminal: false },
  { key: 'byo_ack',         label: 'Moving your number',      component: 'WizardByoAckStep',         terminal: false },
  { key: 'confirm',         label: 'Name and permission',     component: 'WizardConfirmStep',        terminal: false },
  { key: 'number',          label: 'Your WhatsApp number',    component: 'WizardNumberStep',         terminal: false },
  { key: 'handoff',         label: 'Our team takes over',     component: 'WizardHandoffStep',        terminal: false },
  { key: 'connect',         label: 'Sign in with Facebook',   component: 'WizardConnectStep',        terminal: false },
  { key: 'progress',        label: 'Finishing your setup',    component: 'WizardProgressStep',       terminal: false },
  { key: 'live',            label: 'WhatsApp is live',        component: 'WizardLiveStep',           terminal: true },
  { key: 'handoff_pending', label: 'With our team',           component: 'WizardHandoffPendingStep', terminal: true },
  { key: 'blocked',         label: 'Not available',           component: 'WizardBlockedStep',        terminal: true },
];

/** Just the keys, in flow order — what `wizardCopy.js` and the copy-completeness test iterate. */
export const WIZARD_STEP_KEYS = WIZARD_STEPS.map((s) => s.key);

/** The step config for a key, or null. Never throws on an unknown key. */
export function wizardStep(key) {
  return WIZARD_STEPS.find((s) => s.key === key) || null;
}

/** Which component renders `key`. Null for an unknown key, so the shell can fail visibly. */
export function wizardStepComponent(key) {
  const step = wizardStep(key);
  return step ? step.component : null;
}

/** True when a screen has nothing to continue to. */
export function isTerminalStep(key) {
  const step = wizardStep(key);
  return step ? step.terminal === true : false;
}
